import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { Flag, LayoutGrid, Loader2, Map as MapIcon, MountainSnow, Route, Search, X } from "lucide-react";
import { fetchSearch, type SearchResult } from "../api/search";

/**
 * Public site search (ATL-132): one input over peaks, collections, trails,
 * landmarks and regions, backed by GET /search. Works signed out; every result
 * links to the public page, so the signed-in map search (per-user status,
 * fly-to) stays separate. Debounced, keyboard-navigable, results drop under
 * the input.
 */

const CSS = `
.atlas-search{ position:relative; width:100%; font-family:var(--font-sans); }
.atlas-search__field{
  display:flex; align-items:center; gap:10px; height:var(--_h, 48px); padding:0 14px 0 16px;
  background:var(--surface-card); border:1px solid var(--border-default); border-radius:var(--radius-pill);
  box-shadow:var(--shadow-sm); transition:var(--t-colors);
}
.atlas-search__field:focus-within{ border-color:var(--border-gold); box-shadow:var(--glow-gold-sm), var(--shadow-sm); }
.atlas-search__field > svg{ flex:0 0 auto; color:var(--text-muted); }
.atlas-search__input{
  flex:1; min-width:0; height:100%; border:none; outline:none; background:transparent;
  font:inherit; font-size:var(--_fs, 15px); color:var(--text-primary);
}
.atlas-search__input:focus, .atlas-search__input:focus-visible{ outline:none; box-shadow:none; }
.atlas-search__input::placeholder{ color:var(--text-muted); }
.atlas-search__input::-webkit-search-cancel-button, .atlas-search__input::-webkit-search-decoration{ -webkit-appearance:none; appearance:none; display:none; }
.atlas-search__clear{
  display:grid; place-items:center; width:26px; height:26px; border-radius:50%; border:none;
  background:var(--surface-sunken); color:var(--text-muted); cursor:pointer; padding:0;
}
.atlas-search__clear:hover{ color:var(--text-primary); }
.atlas-search__panel{
  position:absolute; top:calc(100% + 8px); left:0; right:0; z-index:40; overflow:hidden;
  background:var(--surface-card); border:1px solid var(--border-subtle); border-radius:var(--radius-lg);
  box-shadow:var(--shadow-lg), var(--ring-top);
}
.atlas-search__list{ list-style:none; margin:0; padding:6px; max-height:min(60vh, 440px); overflow-y:auto; }
.atlas-search__item{
  display:flex; align-items:center; gap:12px; padding:9px 10px; border-radius:var(--radius-md);
  text-decoration:none; color:inherit;
}
.atlas-search__item:hover, .atlas-search__item[aria-selected="true"]{ background:var(--surface-sunken); }
.atlas-search__icon{
  flex:0 0 auto; width:32px; height:32px; border-radius:var(--radius-md); display:grid; place-items:center;
  background:var(--accent-soft); border:1px solid var(--border-gold); color:var(--gold-400);
}
.atlas-search__name{ font-weight:600; font-size:14px; color:var(--text-primary); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.atlas-search__meta{ font-family:var(--font-mono); font-size:10.5px; text-transform:uppercase; letter-spacing:.08em; color:var(--text-muted); margin-top:2px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.atlas-search__empty{ padding:16px 14px; font-size:13.5px; color:var(--text-muted); }
@keyframes atlas-search-spin{ to{ transform:rotate(360deg); } }
.atlas-search__spin{ animation:atlas-search-spin .8s linear infinite; }
@media (prefers-reduced-motion: reduce){ .atlas-search__spin{ animation:none; } }
`;

function ensure() {
  if (typeof document === "undefined") return;
  if (!document.getElementById("atlas-search-css")) {
    const s = document.createElement("style");
    s.id = "atlas-search-css";
    s.textContent = CSS;
    document.head.appendChild(s);
  }
}

const TYPE_LABEL: Record<SearchResult["type"], string> = {
  collection: "Collection",
  peak: "Peak",
  trail: "Trail",
  landmark: "Landmark",
  region: "Region",
};

const COLLECTION_TYPE_LABEL: Record<string, string> = {
  peaks: "Hill list",
  trails: "Trail collection",
  landmarks: "Landmark collection",
};

const COLLECTION_UNIT: Record<string, string> = {
  peaks: "peaks",
  trails: "trails",
  landmarks: "landmarks",
};

function humaniseCategory(category: string): string {
  const s = category.replace(/_/g, " ");
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function resultIcon(type: SearchResult["type"], size = 15): React.ReactNode {
  switch (type) {
    case "peak":
      return <MountainSnow size={size} />;
    case "trail":
      return <Route size={size} />;
    case "landmark":
      return <Flag size={size} />;
    case "region":
      return <MapIcon size={size} />;
    default:
      return <LayoutGrid size={size} />;
  }
}

export function resultMeta(r: SearchResult): string {
  const parts: string[] = [];
  if (r.type === "collection") {
    parts.push(COLLECTION_TYPE_LABEL[r.collectionType ?? ""] ?? TYPE_LABEL.collection);
    if (r.itemCount != null) parts.push(`${r.itemCount} ${COLLECTION_UNIT[r.collectionType ?? ""] ?? "items"}`);
  } else if (r.type === "peak") {
    parts.push(TYPE_LABEL.peak);
    if (r.elevation != null) parts.push(`${r.elevation} m`);
  } else if (r.type === "trail") {
    parts.push(TYPE_LABEL.trail);
    if (r.distanceKm != null) parts.push(`${Math.round(r.distanceKm)} km`);
  } else if (r.type === "landmark") {
    parts.push(r.category ? humaniseCategory(r.category) : TYPE_LABEL.landmark);
  } else {
    parts.push(TYPE_LABEL.region);
  }
  if (r.region) parts.push(r.region);
  return parts.join(" · ");
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = React.useState(value);
  React.useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

interface SiteSearchProps {
  placeholder?: string;
  size?: "md" | "lg";
  autoFocus?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

export function SiteSearch({
  placeholder = "Search peaks, hill lists, trails, landmarks and regions",
  size = "md",
  autoFocus = false,
  className = "",
  style,
}: SiteSearchProps) {
  ensure();
  const navigate = useNavigate();
  const [q, setQ] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const [active, setActive] = React.useState(-1);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const debounced = useDebounced(q.trim(), 180);
  const enabled = debounced.length >= 2;

  const { data, isFetching } = useQuery({
    queryKey: ["search", debounced],
    queryFn: () => fetchSearch(debounced, 12),
    enabled,
    staleTime: 5 * 60 * 1000,
    placeholderData: (prev) => (enabled ? prev : undefined),
  });
  const results = enabled ? data?.results ?? [] : [];

  // Close on outside click.
  React.useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  React.useEffect(() => {
    setActive(-1);
  }, [debounced]);

  const go = (r: SearchResult) => {
    setOpen(false);
    setQ("");
    void navigate({ to: r.href });
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (!results.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActive((i) => Math.min(results.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(-1, i - 1));
    } else if (e.key === "Enter") {
      const r = results[active >= 0 ? active : 0];
      if (r) {
        e.preventDefault();
        go(r);
      }
    }
  };

  const showPanel = open && q.trim().length >= 2;
  const fieldVars = size === "lg" ? ({ "--_h": "56px", "--_fs": "17px" } as React.CSSProperties) : undefined;

  return (
    <div ref={rootRef} className={["atlas-search", className].filter(Boolean).join(" ")} style={style} role="search">
      <div className="atlas-search__field" style={fieldVars}>
        {isFetching && enabled ? <Loader2 size={18} className="atlas-search__spin" /> : <Search size={18} />}
        <input
          className="atlas-search__input"
          type="search"
          value={q}
          placeholder={placeholder}
          autoFocus={autoFocus}
          autoComplete="off"
          spellCheck={false}
          aria-label="Search Atlas"
          aria-expanded={showPanel}
          aria-controls="atlas-search-results"
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
        />
        {q && (
          <button
            type="button"
            className="atlas-search__clear"
            aria-label="Clear search"
            onClick={() => {
              setQ("");
              setOpen(false);
            }}
          >
            <X size={14} />
          </button>
        )}
      </div>

      {showPanel && (
        <div className="atlas-search__panel">
          {results.length === 0 ? (
            <div className="atlas-search__empty">
              {enabled && !isFetching ? `Nothing called "${q.trim()}" yet. Try a peak, a hill list or a trail.` : "Searching…"}
            </div>
          ) : (
            <ul className="atlas-search__list" id="atlas-search-results" role="listbox">
              {results.map((r, i) => (
                <li key={`${r.type}:${r.slug}`} role="option" aria-selected={i === active}>
                  <Link
                    to={r.href}
                    className="atlas-search__item"
                    aria-selected={i === active}
                    onMouseEnter={() => setActive(i)}
                    onClick={(e) => {
                      e.preventDefault();
                      go(r);
                    }}
                  >
                    <span className="atlas-search__icon">{resultIcon(r.type)}</span>
                    <span style={{ minWidth: 0, flex: 1 }}>
                      <span className="atlas-search__name" style={{ display: "block" }}>
                        {r.name}
                      </span>
                      <span className="atlas-search__meta" style={{ display: "block" }}>
                        {resultMeta(r)}
                      </span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
